import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  isAuthenticated: false,
  user: {
    id: 1,
    first_name: 'John',
    last_name: 'Doe',
    is_superuser: true,
    name: 'John Doe',
    email: 'johndoe@test.com',
    hasSupportTicketsAccess: 0,
    hasClientMeetingsAccess: 0,
    hasDocuSyncAccess: 0,
    hasDocuCheckAccess: 0,
  },
  messagesCount: 0,
  unreadTasksCount: 0,
  unreadTaskVaultProjectIds: [],
  unreadRequestsCount: 0,
  unreadRequestProjectIds: [],
  unreadDocuSyncCount: 0,
  unreadDocuSyncFolderIds: [],
  unreadContactsCount: 0,
  unreadContactIds: [],
  hasSupportTicketsAccess: 0,
  hasClientMeetingsAccess: 0,
  hasDocuSyncAccess: 0,
  hasDocuCheckAccess: 0,
  transactionsCount: 0,
  unreadTransactionIds: [],
  unreadTransactionalQuestionsCount: 0,
  unreadTransactionalQuestionIds: [],
  unreadPasswordRequestsCount: 0,
  unreadPasswordRequestIds: [],
  unreadDocumentsCount: 0,
  unreadDocumentItems: [],
  companyName: '',
  logo: '',
  isForcePassword: 0,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    onLoggedIn: (state, action) => {
      const {
        user,
        access_token: token,
        unread_message_count: messagesCount,
        unread_tasks_count: unreadTasksCount,
        unread_project_ids: unreadTaskVaultProjectIds,
        unread_requests_count: unreadRequestsCount,
        unread_request_project_ids: unreadRequestProjectIds,
        unread_docusync_folders_count: unreadDocuSyncCount,
        unread_docusync_folder_ids: unreadDocuSyncFolderIds,
        unread_contacts_count: unreadContactsCount,
        unread_contact_ids: unreadContactIds,
        transactions_count: transactionsCount,
        unread_transaction_ids: unreadTransactionIds,
        unread_transactional_questions_count: unreadTransactionalQuestionsCount,
        unread_transactional_question_ids: unreadTransactionalQuestionIds,
        unread_password_requests_count: unreadPasswordRequestsCount,
        unread_password_request_ids: unreadPasswordRequestIds,
        unread_documents_count: unreadDocumentsCount,
        unread_document_items: unreadDocumentItems,
        company_name: companyName,
        has_support_tickets_access: hasSupportTicketsAccess,
        has_client_meetings_access: hasClientMeetingsAccess,
        has_docsync_access: hasDocuSyncAccess,
        has_docucheck_access: hasDocuCheckAccess,
        company_logo: logo,
        is_force_password: isForcePassword,
      } = action.payload;
      localStorage.setItem('token', token);
      return {
        ...state,
        isAuthenticated: true,
        has_support_tickets_access: hasSupportTicketsAccess,
        has_client_meetings_access: hasClientMeetingsAccess,
        has_docsync_access: hasDocuSyncAccess,
        has_docucheck_access: hasDocuCheckAccess,
        user,
        messagesCount,
        unreadTasksCount,
        unreadTaskVaultProjectIds: unreadTaskVaultProjectIds ?? [],
        unreadRequestsCount,
        unreadRequestProjectIds: unreadRequestProjectIds ?? [],
        unreadDocuSyncCount: unreadDocuSyncCount ?? 0,
        unreadDocuSyncFolderIds: unreadDocuSyncFolderIds ?? [],
        unreadContactsCount: unreadContactsCount ?? 0,
        unreadContactIds: unreadContactIds ?? [],
        transactionsCount: transactionsCount ?? 0,
        unreadTransactionIds: unreadTransactionIds ?? [],
        unreadTransactionalQuestionsCount: unreadTransactionalQuestionsCount ?? 0,
        unreadTransactionalQuestionIds: unreadTransactionalQuestionIds ?? [],
        unreadPasswordRequestsCount: unreadPasswordRequestsCount ?? 0,
        unreadPasswordRequestIds: unreadPasswordRequestIds ?? [],
        unreadDocumentsCount: unreadDocumentsCount ?? 0,
        unreadDocumentItems: unreadDocumentItems ?? [],
        companyName,
        logo,
        is_force_password: isForcePassword,
      };
    },
    getUserDetail: (state, action) => {
      const {
        unread_message_count: messagesCount,
        unread_tasks_count: unreadTasksCount,
        unread_project_ids: unreadTaskVaultProjectIds,
        unread_requests_count: unreadRequestsCount,
        unread_request_project_ids: unreadRequestProjectIds,
        unread_docusync_folders_count: unreadDocuSyncCount,
        unread_docusync_folder_ids: unreadDocuSyncFolderIds,
        unread_contacts_count: unreadContactsCount,
        unread_contact_ids: unreadContactIds,
        transactions_count: transactionsCount,
        unread_transaction_ids: unreadTransactionIds,
        unread_transactional_questions_count: unreadTransactionalQuestionsCount,
        unread_transactional_question_ids: unreadTransactionalQuestionIds,
        unread_password_requests_count: unreadPasswordRequestsCount,
        unread_password_request_ids: unreadPasswordRequestIds,
        unread_documents_count: unreadDocumentsCount,
        unread_document_items: unreadDocumentItems,
        company_name: companyName,
        logo,
        ...restPayload
      } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user: restPayload,
        messagesCount,
        unreadTasksCount,
        unreadTaskVaultProjectIds: unreadTaskVaultProjectIds ?? [],
        unreadRequestsCount,
        unreadRequestProjectIds: unreadRequestProjectIds ?? [],
        unreadDocuSyncCount: unreadDocuSyncCount ?? 0,
        unreadDocuSyncFolderIds: unreadDocuSyncFolderIds ?? [],
        unreadContactsCount: unreadContactsCount ?? 0,
        unreadContactIds: unreadContactIds ?? [],
        transactionsCount,
        unreadTransactionIds: unreadTransactionIds ?? [],
        unreadTransactionalQuestionsCount: unreadTransactionalQuestionsCount ?? 0,
        unreadTransactionalQuestionIds: unreadTransactionalQuestionIds ?? [],
        unreadPasswordRequestsCount: unreadPasswordRequestsCount ?? 0,
        unreadPasswordRequestIds: unreadPasswordRequestIds ?? [],
        unreadDocumentsCount: unreadDocumentsCount ?? 0,
        unreadDocumentItems: unreadDocumentItems ?? [],
        companyName,
        logo,
      };
    },
    onLoggedOut: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('universalLogin');

      return { ...initialState };
    },
    updateMessageCount: state => ({
      ...state,
      messagesCount: 0,
    }),
    updateTaskVaultCount: (state, action) => ({
      ...state,
      unreadTasksCount: action?.payload ?? 0,
    }),
    updateTaskVaultNotifications: (state, action) => ({
      ...state,
      unreadTasksCount: action?.payload?.count ?? 0,
      unreadTaskVaultProjectIds: action?.payload?.projectIds ?? [],
    }),
    syncTaskVaultSidebarCount: state => ({
      ...state,
      unreadTasksCount: state.unreadTaskVaultProjectIds.length,
    }),
    updateRequestPlusCount: (state, action) => ({
      ...state,
      unreadRequestsCount: action?.payload ?? 0,
    }),
    updateRequestPlusNotifications: (state, action) => ({
      ...state,
      unreadRequestsCount: action?.payload?.count ?? 0,
      unreadRequestProjectIds: action?.payload?.projectIds ?? [],
    }),
    syncRequestPlusSidebarCount: state => ({
      ...state,
      unreadRequestsCount: state.unreadRequestProjectIds.length,
    }),
    updateDocuSyncNotifications: (state, action) => ({
      ...state,
      unreadDocuSyncCount: action?.payload?.count ?? 0,
      unreadDocuSyncFolderIds: action?.payload?.folderIds ?? [],
    }),
    syncDocuSyncSidebarCount: state => ({
      ...state,
      unreadDocuSyncCount: state.unreadDocuSyncFolderIds.length,
    }),
    updateContactsNotifications: (state, action) => ({
      ...state,
      unreadContactsCount: action?.payload?.count ?? 0,
      unreadContactIds: action?.payload?.contactIds ?? [],
    }),
    syncContactsSidebarCount: state => ({
      ...state,
      unreadContactsCount: state.unreadContactIds.length,
    }),
    updateTransactionsCount: (state, action) => ({
      ...state,
      transactionsCount: action?.payload ?? 0,
    }),
    updateTransactionsNotifications: (state, action) => ({
      ...state,
      transactionsCount: action?.payload?.count ?? 0,
      unreadTransactionIds: action?.payload?.transactionIds ?? [],
    }),
    syncTransactionsSidebarCount: state => ({
      ...state,
      transactionsCount: state.unreadTransactionIds.length,
    }),
    updateTransactionalQuestionsNotifications: (state, action) => ({
      ...state,
      unreadTransactionalQuestionsCount: action?.payload?.count ?? 0,
      unreadTransactionalQuestionIds: action?.payload?.questionIds ?? [],
    }),
    updatePasswordRequestsCount: (state, action) => ({
      ...state,
      unreadPasswordRequestsCount: action?.payload ?? 0,
    }),
    updatePasswordRequestsNotifications: (state, action) => ({
      ...state,
      unreadPasswordRequestsCount: action?.payload?.count ?? 0,
      unreadPasswordRequestIds: action?.payload?.requestIds ?? [],
    }),
    syncPasswordRequestsSidebarCount: state => ({
      ...state,
      unreadPasswordRequestsCount: state.unreadPasswordRequestIds.length,
    }),
    updateDocumentsNotifications: (state, action) => ({
      ...state,
      unreadDocumentsCount: action?.payload?.count ?? 0,
      unreadDocumentItems: action?.payload?.items ?? [],
    }),
    syncDocumentsSidebarCount: state => ({
      ...state,
      unreadDocumentsCount: state.unreadDocumentItems.length,
    }),
    clearForcePassword: state => ({
      ...state,
      isForcePassword: 0,
      user: {
        ...state.user,
        is_force_password: 0,
      },
    }),
  },
});

export const {
  onLoggedIn,
  getUserDetail,
  onLoggedOut,
  updateMessageCount,
  updateTaskVaultCount,
  updateTaskVaultNotifications,
  syncTaskVaultSidebarCount,
  updateRequestPlusCount,
  updateRequestPlusNotifications,
  syncRequestPlusSidebarCount,
  updateDocuSyncNotifications,
  syncDocuSyncSidebarCount,
  updateContactsNotifications,
  syncContactsSidebarCount,
  updateTransactionsCount,
  updateTransactionsNotifications,
  syncTransactionsSidebarCount,
  updateTransactionalQuestionsNotifications,
  updatePasswordRequestsNotifications,
  syncPasswordRequestsSidebarCount,
  updateDocumentsNotifications,
  syncDocumentsSidebarCount,
  clearForcePassword,
} = authSlice.actions;
export default authSlice.reducer;
